import { useFormContext } from "@birdwatch/ui/form";
import type { AnyFieldApi, AnyFieldMeta } from "@tanstack/react-form";
import type * as React from "react";
import type { ReactNode } from "react";

/**
 * The slice of TanStack's field API the catalog wrappers use, with the value
 * narrowed to `TValue`.
 *
 * The BINDING prop stays `AnyFieldApi` (below) — its `value: any` is assignable
 * to every `FieldOf<T>`, so render-prop call sites are unaffected. The narrowing
 * exists so wrapper bodies stop casting `field.state.value as string` and so a
 * `handleChange` of the wrong type is a compile error instead of an `as any`.
 *
 * `validate` is deliberately NOT a member: the discrete-commit cadence is
 * `commitFieldValue`, so leaving it off makes a relapse a compile error.
 */
export type FieldOf<TValue> = {
  name: string;
  state: { value: TValue; meta: AnyFieldMeta };
  handleChange: (value: TValue) => void;
  handleBlur: AnyFieldApi["handleBlur"];
};

export type PolyFieldChildren<TValue> = (field: FieldOf<TValue>) => ReactNode;

type PolyFieldProps<TValue> = {
  name?: string;
  field?: AnyFieldApi | FieldOf<any>;
  children: PolyFieldChildren<TValue>;
};

export function PolyField<TValue = unknown>({
  name,
  field,
  children,
}: PolyFieldProps<TValue>) {
  if (field) return <>{children(field)}</>;
  if (!name) {
    throw new Error(
      "PolyField requires either `name` (terse mode) or `field` (render-prop mode)",
    );
  }
  return <PolyFieldWithName<TValue> name={name}>{children}</PolyFieldWithName>;
}

function PolyFieldWithName<TValue>({
  name,
  children,
}: {
  name: string;
  children: PolyFieldChildren<TValue>;
}) {
  // `useFormContext()` is typed against the empty base form (`Record<string, never>`),
  // so `AppField` is not on its declared type — the runtime value provided by
  // `<form.AppForm>` always is the app form api. This is the one cast in the catalog.
  const form = useFormContext() as unknown as {
    AppField: React.ComponentType<{
      name: string;
      children: (field: AnyFieldApi) => ReactNode;
    }>;
  };
  return (
    <form.AppField name={name}>{(field) => children(field)}</form.AppField>
  );
}

export type FieldBindingProps =
  | { name: string; field?: never }
  | { field: AnyFieldApi | FieldOf<any>; name?: never };

export function pickBinding<P extends FieldBindingProps>(
  props: P,
): { name?: string; field?: AnyFieldApi | FieldOf<any> } {
  if ("field" in props && props.field) return { field: props.field };
  if ("name" in props && props.name) return { name: props.name };
  throw new Error("Field requires either `name` or `field` prop");
}

/**
 * Same as {@link pickBinding}, plus the remaining props with `name` / `field`
 * stripped — for wrappers that rest-spread onto a DOM element, where a leaked
 * `field` object would reach the DOM as an unknown attribute.
 */
export function splitBinding<P extends FieldBindingProps>(props: P) {
  const { name: _name, field: _field, ...rest } = props;
  return [pickBinding(props), rest] as const;
}
